import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, BookOpen, RefreshCw, CheckCircle2, Tag, Gift, CreditCard, Star } from 'lucide-react';
import { PhoneImage } from '../components/site/PhoneImage';
import { useT } from '../components/site/i18n';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-1075" data-mp="2vg23830kdvp3">
    {children}
  </motion.div>;
export function ParaVoce() {
  const t = useT();
  return <div className="min-h-screen bg-brand-cream dark:bg-gray-900 pt-24 relative overflow-hidden transition-colors duration-300" data-id="element-1076" data-mp="3ec1mmpfh7qx2">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }} data-id="element-1077" data-mp="3q2j2yhf8yd75"></div>

      {/* Hero Section */}
      <section className="px-6 py-16 md:py-24 max-w-7xl mx-auto text-center relative z-10" data-id="element-1078" data-mp="3u29z70njllj2">
        <FadeIn data-id="element-1079" data-mp="cm1thbd4mat8">
          <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-6 flex items-center justify-center gap-2" data-id="element-1080" data-mp="cljpfsk82sar">
            <MapPin size={16} className="text-brand-yellow" data-id="element-1081" data-mp="lo1cuabm5qzc" />
            {t.paraVoce.heroEyebrow}
          </span>
          <h1 className="text-5xl md:text-7xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-8 leading-[1.1] tracking-tight max-w-4xl mx-auto" data-id="element-1082" data-mp="hmzdlthp5exd">
            {t.paraVoce.heroTitle1}{' '}
            <span className="relative inline-block" data-id="element-1083" data-mp="2jecfe9odb6p7">
              <span className="relative z-10 font-display text-brand-green dark:text-brand-green-light font-normal text-6xl md:text-8xl tracking-normal transform -rotate-2 inline-block" data-id="element-1084" data-mp="mcog2d5upntc">
                {t.paraVoce.heroTitle2}
              </span>
              {/* SVG Highlight */}
              <svg className="absolute -bottom-2 left-0 w-full h-4 text-brand-yellow -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-1085" data-mp="wcxiw9ucm7hp">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" aria-hidden="true" data-id="element-1086" data-mp="159ol73zhuikq" />
              </svg>
            </span>
          </h1>
          <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 font-sans mb-12 leading-relaxed max-w-2xl mx-auto" data-id="element-1087" data-mp="1ozi9h8302fk7">
            {t.paraVoce.heroDesc}
          </p>
          <span className="inline-block bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light px-8 py-4 rounded-full text-sm font-bold tracking-wide uppercase cursor-default select-none border border-brand-green/20 dark:border-brand-green/30" data-id="element-1088" data-mp="1sz0s1x7cmejv">
            {t.common.disponivelEmBreve}
          </span>
        </FadeIn>
      </section>

      {/* Benefits Grid */}
      <section className="bg-white dark:bg-gray-800 py-24 px-6 relative z-10 transition-colors duration-300" data-id="element-1089" data-mp="2zlhbnxdymrl4">
        <div className="max-w-7xl mx-auto" data-id="element-1090" data-mp="13k4q5wx4302">
          <FadeIn className="text-center mb-20" data-id="element-1091" data-mp="3s7sdtch7t0k1">
            <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-1092" data-mp="3sa4zo0q3fv2d">
              {t.paraVoce.benefitsEyebrow}
            </span>
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-1093" data-mp="3ig4p3jc1lxm9">
              {t.paraVoce.benefitsTitle}
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1094" data-mp="ezp67ea18iz4">
            {[{
            icon: <Tag size={24} data-id="element-1095" data-mp="3l43jcpeg9v8" />,
            title: t.paraVoce.ben1Title,
            desc: t.paraVoce.ben1Desc
          }, {
            icon: <Gift size={24} data-id="element-1096" data-mp="3lsa3burgr36p" />,
            title: t.paraVoce.ben2Title,
            desc: t.paraVoce.ben2Desc
          }, {
            icon: <MapPin size={24} data-id="element-1097" data-mp="i9qg56nn6nir" />,
            title: t.paraVoce.ben3Title,
            desc: t.paraVoce.ben3Desc
          }, {
            icon: <BookOpen size={24} data-id="element-1098" data-mp="nawata0v4kux" />,
            title: t.paraVoce.ben4Title,
            desc: t.paraVoce.ben4Desc
          }, {
            icon: <RefreshCw size={24} data-id="element-1099" data-mp="dhpai3kzbv1p" />,
            title: t.paraVoce.ben5Title,
            desc: t.paraVoce.ben5Desc
          }, {
            icon: <CreditCard size={24} data-id="element-1100" data-mp="15zujdcwx4l98" />,
            title: t.paraVoce.ben6Title,
            desc: t.paraVoce.ben6Desc
          }].map((benefit, i) => <FadeIn key={i} delay={i * 0.1} className="bg-brand-cream/50 dark:bg-gray-900/50 p-8 rounded-3xl border-t-4 border-t-brand-yellow border-x border-b border-gray-100 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-900 hover:shadow-md transition-all duration-300 relative overflow-hidden group" data-id="element-1101" data-mp="2lr557z5dtr7w" data-mpi={i}>
                <div className="absolute top-0 right-0 w-16 h-16 bg-brand-yellow/10 dark:bg-brand-yellow/5 rounded-bl-full -z-10 group-hover:scale-150 transition-transform duration-500" data-id="element-1102" data-mp="27syrm4nv2nzo"></div>
                <div className="w-12 h-12 bg-white dark:bg-gray-800 text-brand-green-dark dark:text-brand-green-light rounded-xl flex items-center justify-center mb-6 shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-1103" data-mp="2gpplqbiursrm">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-1104" data-mp="3n7co2dj18keu">
                  {benefit.title}
                </h3>
                <p className="text-brand-charcoal/60 dark:text-gray-300 leading-relaxed" data-id="element-1105" data-mp="2ptaxw9y1jj4g">
                  {benefit.desc}
                </p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Wallet Section */}
      <section className="py-24 px-6 max-w-7xl mx-auto relative z-10" data-id="element-1106" data-mp="2ytpn3ke4lid8">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-1107" data-mp="224rti5i43qhn">
          <FadeIn className="relative flex justify-center lg:justify-start order-2 lg:order-1" data-id="element-1108" data-mp="265k2qyqzhvdx">
            <div className="absolute inset-0 bg-brand-green/5 dark:bg-brand-green/10 rounded-full blur-3xl -z-10 transform scale-125" data-id="element-1109" data-mp="2b4n8klesg5my"></div>
            <PhoneImage src="https://cdn.magicpatterns.com/uploads/1qG5Jc5HaRfTomzFyUkFHw/Wallet.png" alt="Carteira digital do BairrouApp" className="transform rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-1110" data-mp="2fwexysm7k5bi" />
          </FadeIn>
          <FadeIn className="order-1 lg:order-2" data-id="element-1111" data-mp="1fvtadsng6fgp">
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-1112" data-mp="1ft76q43ogk3r">
              {t.paraVoce.walletTitle}
            </h2>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 mb-10 leading-relaxed" data-id="element-1113" data-mp="15vkfqj93t23f">
              {t.paraVoce.walletDesc}
            </p>
            <ul className="space-y-6" data-id="element-1114" data-mp="wwyw0krwbh12">
              {[t.paraVoce.walletList1, t.paraVoce.walletList2, t.paraVoce.walletList3].map((item, i) => <li key={i} className="flex items-center gap-4" data-id="element-1115" data-mp="n7xfmthfuxmk" data-mpi={i}>
                  <div className="w-8 h-8 rounded-full bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light flex items-center justify-center shrink-0" data-id="element-1116" data-mp="i82t124p4u7b">
                    <CheckCircle2 size={16} data-id="element-1117" data-mp="974p0ju9cmoq" />
                  </div>
                  <span className="text-brand-charcoal dark:text-gray-200 font-medium" data-id="element-1118" data-mp="97wfjg8zzu85">
                    {item}
                  </span>
                </li>)}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* How to Earn Points */}
      <section className="bg-brand-charcoal dark:bg-gray-950 text-white py-24 px-6 relative overflow-hidden transition-colors duration-300" data-id="element-1119" data-mp="1o2cmqe8x069i">
        {/* Organic shape background */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-green/20 dark:bg-brand-green/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" aria-hidden="true" data-id="element-1120" data-mp="1ed1f6w1of9nq"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-1121" data-mp="14gq4ublnctvx">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20" data-id="element-1122" data-mp="19dowmnhu8oqy">
            <FadeIn data-id="element-1123" data-mp="vegtjjik0whh">
              <span className="text-brand-green font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-1124" data-mp="vdxbm2swm83g">
                {t.paraVoce.howToEyebrow}
              </span>
              <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-white mb-6 tracking-tight" data-id="element-1125" data-mp="10fcwnuv30elu">
                {t.paraVoce.howToTitle}
              </h2>
              <p className="text-lg text-white/60 max-w-2xl mx-auto leading-relaxed" data-id="element-1126" data-mp="bu861q1sys5y">
                {t.paraVoce.howToDesc}
              </p>
            </FadeIn>
            <FadeIn delay={0.2} className="relative flex justify-center lg:justify-end" data-id="element-1127" data-mp="lqbhjmijo35k">
              <div className="absolute inset-0 bg-brand-yellow/10 dark:bg-brand-yellow/5 rounded-full blur-3xl -z-10 transform scale-125" data-id="element-1128" data-mp="zpb5ery8ge0o"></div>
              <PhoneImage src="https://cdn.magicpatterns.com/uploads/rBKDBrCYsC8iZEEEAuogN1/Impact.png" alt={t.paraVoce.mockAlt2} className="transform -rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-1129" data-mp="19gflxw3k46xm" />
            </FadeIn>
          </div>

          <div className="grid md:grid-cols-4 gap-8 relative" data-id="element-1130" data-mp="1diakwty0m888">
            {/* Dotted connector line (desktop only) */}
            <div className="hidden md:block absolute top-12 left-1/8 right-1/8 h-0.5 border-t-2 border-dashed border-white/20 dark:border-white/10 z-0" aria-hidden="true" data-id="element-1131" data-mp="3vvvm5k7nja55"></div>

            {[{
            step: '1',
            title: t.paraVoce.step1Title,
            desc: t.paraVoce.step1Desc
          }, {
            step: '2',
            title: t.paraVoce.step2Title,
            desc: t.paraVoce.step2Desc
          }, {
            step: '3',
            title: t.paraVoce.step3Title,
            desc: t.paraVoce.step3Desc
          }, {
            step: '4',
            title: t.paraVoce.step4Title,
            desc: t.paraVoce.step4Desc
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} className="relative z-10" data-id="element-1132" data-mp="2oi21o0o2eujp" data-mpi={i}>
                <div className="bg-white/5 dark:bg-white/5 p-8 rounded-3xl border border-white/10 dark:border-white/5 h-full hover:bg-white/10 dark:hover:bg-white/10 transition-colors" data-id="element-1133" data-mp="2ok6dr5w4azv2">
                  <div className="w-12 h-12 bg-brand-yellow text-brand-charcoal font-bold text-xl rounded-full flex items-center justify-center mb-6 shadow-md" data-id="element-1134" data-mp="18znp8f2nja19">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold mb-3" data-id="element-1135" data-mp="1ir0f514w3q4n">{item.title}</h3>
                  <p className="text-white/60 leading-relaxed" data-id="element-1136" data-mp="1nnh23s0cryg5">{item.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-32 px-6 bg-white dark:bg-gray-800 relative z-10 transition-colors duration-300" data-id="element-1137" data-mp="1wp85urbc1v7d">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1138" data-mp="21n06qatygrnp">
          <FadeIn data-id="element-1139" data-mp="2bh228foq5ju">
            <div className="flex justify-center gap-1 mb-10 text-brand-yellow" data-id="element-1140" data-mp="2bz27g5gx3x1">
              {[...Array(5)].map((_, i) => <Star key={i} size={28} fill="currentColor" data-id="element-1141" data-mp="7buzkwjntx3m" data-mpi={i} />)}
            </div>
            <p className="text-4xl md:text-5xl font-display text-brand-charcoal dark:text-white mb-10 leading-relaxed tracking-normal transform -rotate-1" data-id="element-1142" data-mp="3vgjgfp5h5m06">
              {t.paraVoce.testimonialQuote}
            </p>
            <p className="text-brand-charcoal/60 dark:text-gray-400 font-bold tracking-wide uppercase text-sm" data-id="element-1143" data-mp="485lgxw6y7yu">
              {t.paraVoce.testimonialAuthor}
            </p>
          </FadeIn>
        </div>
      </section>
    </div>;
}